import { PropsWithChildren } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, defineMessage, useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import Link from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";

import { CardIcon, TitleCard } from "./components/card";
import { MotionContainer } from "./components/motion-container";
import { RowGrid } from "./layouts/row-grid";
import { LinkColumn } from "./layouts/subnav-link-columns";
import { useCurrentNav } from "./nav-links-container";

type ResourceData = {
    id: string;
    title: ReturnType<typeof defineMessage>;
    content: ReturnType<typeof defineMessage>;
    href: string;
    icon: CardIcon;
};

const icons = {
    customerSupport: {
        normal: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
            >
                <g clipPath="url(#clip0_1433_4464)">
                    <path
                        d="M12 5.14286C12 7.98318 9.69746 10.2857 6.85714 10.2857C4.01682 10.2857 1.71429 7.98318 1.71429 5.14286C1.71429 2.30254 4.01682 0 6.85714 0C9.69746 0 12 2.30254 12 5.14286Z"
                        fill="#82B536"
                    />
                    <path
                        d="M0 19.7143C0 15.9272 3.07005 12.8571 6.85714 12.8571C10.6442 12.8571 13.7143 15.9272 13.7143 19.7143V24H0V19.7143Z"
                        fill="#82B536"
                    />
                    <path
                        d="M16.2856 16.1386L24.3549 8.06924L21.9305 5.64488L16.2856 11.2898L14.0693 9.07346L11.6449 11.4978L16.2856 16.1386Z"
                        fill="#82B536"
                    />
                </g>
                <defs>
                    <clipPath id="clip0_1433_4464">
                        <rect width="24" height="24" fill="white" />
                    </clipPath>
                </defs>
            </svg>
        )
    },
    findPartners: {
        normal: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
            >
                <path
                    d="M18.6667 9V6.33333H16V3.66667H18.6667V1H21.3333V3.66667H24V6.33333H21.3333V9H18.6667Z"
                    fill="#BF63F3"
                />
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.33333 3.66667C11.0336 3.66667 12.6276 4.1213 14.0006 4.91564C14.0002 4.94371 14 4.97183 14 5C14 7.76043 15.8641 10.0855 18.4022 10.7849C18.5751 11.495 18.6667 12.2368 18.6667 13C18.6667 18.1547 14.488 22.3333 9.33333 22.3333C4.17868 22.3333 0 18.1547 0 13C0 7.84534 4.17868 3.66667 9.33333 3.66667ZM6.66667 9.66667C7.40305 9.66667 8 10.2636 8 11C8 11.7364 7.40305 12.3333 6.66667 12.3333C5.93029 12.3333 5.33333 11.7364 5.33333 11C5.33333 10.2636 5.93029 9.66667 6.66667 9.66667ZM12 9.66667C12.7364 9.66667 13.3333 10.2636 13.3333 11C13.3333 11.7364 12.7364 12.3333 12 12.3333C11.2636 12.3333 10.6667 11.7364 10.6667 11C10.6667 10.2636 11.2636 9.66667 12 9.66667ZM5.99983 14.9191L6.70693 15.6262C8.15745 17.0767 10.5092 17.0767 11.9597 15.6262L12.6668 14.9191L14.081 16.3333L13.3739 17.0404C11.1424 19.272 7.52428 19.272 5.29272 17.0404L4.58561 16.3333L5.99983 14.9191Z"
                    fill="#BF63F3"
                />
            </svg>
        )
    },
    migrationProgram: {
        normal: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
            >
                <g clipPath="url(#clip0_1433_4490)">
                    <path
                        d="M24 0L0 8.21236L9.0852 12.7553L14.2501 7.59204L16.3711 9.71371L11.2068 14.8763L15.7499 23.9624L24 0Z"
                        fill="#FFA900"
                    />
                </g>
                <defs>
                    <clipPath id="clip0_1433_4490">
                        <rect width="24" height="24" fill="white" />
                    </clipPath>
                </defs>
            </svg>
        )
    },
    university: {
        normal: (
            <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
            >
                <path
                    d="M19.5294 0H6.82353C5.26414 0 4 1.26414 4 2.82353V17.6471C4 19.2065 5.26414 20.4706 6.82353 20.4706H9.64706V24L12.4706 21.8824L15.2941 24V20.4706H19.5294V17.6471H15.2941V15.5294H9.64706V17.6471H8.94118C7.77163 17.6471 6.82353 16.699 6.82353 15.5294C6.82353 14.3599 7.77163 13.4118 8.94118 13.4118H19.5294V0Z"
                    fill="#82B536"
                />
            </svg>
        )
    }
} as const;

function useResourcesData(): Array<ResourceData> {
    return [
        {
            id: "resources.customer-support",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.customer-support.title",
                defaultMessage: "Customer Support"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.customer-support.content",
                defaultMessage: "Ask questions, report bugs & give us feedback"
            }),
            href: "//support.atlassian.com/",
            icon: icons.customerSupport
        },
        {
            id: "resources.find-partners",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.find-partners.title",
                defaultMessage: "Find Partners"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.find-partners.content",
                defaultMessage:
                    "Consulting, training & product customization support"
            }),
            href: "//partnerdirectory.atlassian.com/",
            icon: icons.findPartners
        },
        {
            id: "resources.migration-program",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.migration-program.title",
                defaultMessage: "Migration Program"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.migration-program.content",
                defaultMessage:
                    "Step-by-step guidance for your Atlassian cloud migration"
            }),
            href: useLocalizedHref("/migration"),
            icon: icons.migrationProgram
        },
        {
            id: "resources.university",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.university.title",
                defaultMessage: "University"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.resources.university.content",
                defaultMessage:
                    "Learn and expand your skill set for all things Atlassian"
            }),
            href: "//university.atlassian.com/",
            icon: icons.university
        }
    ];
}

function useResourcesLinks() {
    return {
        support: {
            id: "support",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.links.support",
                defaultMessage: "Support"
            }),
            items: [
                {
                    id: "general-inquiries",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.general-inquiries",
                        defaultMessage: "General Inquiries"
                    }),
                    href: useLocalizedHref("/company/contact/general-inquiries")
                },
                {
                    id: "technical-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.technical-support",
                        defaultMessage: "Technical Support"
                    }),
                    href: "//support.atlassian.com/contact/"
                },
                {
                    id: "product-advice",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.product-advice",
                        defaultMessage: "Product Advice"
                    }),
                    href: useLocalizedHref(
                        "/company/contact/product-evaluator-advice"
                    )
                },
                {
                    id: "pricing-billing",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.pricing-billing",
                        defaultMessage: "Pricing and Billing"
                    }),
                    href: useLocalizedHref(
                        "/company/contact/purchasing-licensing"
                    )
                },
                {
                    id: "partner-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.partner-support",
                        defaultMessage: "Partner Support"
                    }),
                    // TODO: FIGURE OUT LINK
                    href: useLocalizedHref("/")
                },
                {
                    id: "developer-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.developer-support",
                        defaultMessage: "Developer Support"
                    }),
                    href: "//developer.atlassian.com/"
                },
                {
                    id: "enterprise-support",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.enterprise-support",
                        defaultMessage: "Enterprise Support"
                    }),
                    href: useLocalizedHref("/enterprise/services")
                },
                {
                    id: "purchasing-licensing",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.purchasing-billing",
                        defaultMessage: "Purchasing and Licensing"
                    }),
                    href: useLocalizedHref("/licensing/purchase-licensing")
                }
            ]
        },
        learn: {
            id: "learn",
            title: defineMessage({
                id: "globalnavv2.subnav.resources.links.learn",
                defaultMessage: "Learn"
            }),
            items: [
                {
                    id: "project-management",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.project-management",
                        defaultMessage: "Project Management"
                    }),
                    href: useLocalizedHref("/")
                },
                {
                    id: "project-collaboration",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.project-collaboration",
                        defaultMessage: "Project Collaboration"
                    }),
                    href: useLocalizedHref("/")
                },
                {
                    id: "agile",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.agile",
                        defaultMessage: "Agile"
                    }),
                    href: useLocalizedHref("/agile")
                },
                {
                    id: "team-playbook",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.team-playbook",
                        defaultMessage: "Team Playbook"
                    }),
                    href: useLocalizedHref("/team-playbook")
                },
                {
                    id: "community",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.community",
                        defaultMessage: "Community"
                    }),
                    href: useLocalizedHref("/community")
                },
                {
                    id: "product-documentation",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.product-documentation",
                        defaultMessage: "Product Documentation"
                    }),
                    href: "//confluence.atlassian.com/display/ALLDOC/Atlassian+Documentation"
                },
                {
                    id: "get-started",
                    text: defineMessage({
                        id: "globalnavv2.subnav.resources.links.get-started",
                        defaultMessage: "Get Started"
                    }),
                    href: useLocalizedHref("/get-started")
                }
            ]
        }
    } as const;
}

const ResourcesGrid = ({ children }: PropsWithChildren) => {
    return (
        <RowGrid
            numCols={4}
            rowGap={token("space.500")}
            columnGap={token("space.400")}
            paddingBlock={token("space.400")}
            paddingInline={token("space.300")}
        >
            {children}
        </RowGrid>
    );
};

export const ResourcesSubnav = () => {
    const intl = useIntl();

    const resourcesData = useResourcesData();
    const resourcesLinks = useResourcesLinks();
    const linkColumns = Object.values(resourcesLinks);

    const { direction } = useCurrentNav();

    return (
        <TypographyStyles>
            <MotionContainer direction={direction}>
                <ResourcesGrid>
                    {resourcesData.map(({ id, title, content, ...rest }) => (
                        <TitleCard
                            key={id}
                            title={intl.formatMessage(title)}
                            content={intl.formatMessage(content)}
                            {...rest}
                        />
                    ))}
                </ResourcesGrid>
                <div css={css({ backgroundColor: "#F8F8F8" })}>
                    <RowGrid
                        numCols={2}
                        paddingBlock={token("space.400")}
                        paddingInline={token("space.400")}
                    >
                        {linkColumns.map((column) => {
                            return (
                                <LinkColumn key={column.id}>
                                    <span
                                        css={css({
                                            "textTransform": "uppercase",
                                            "color": "#101214",
                                            "marginBlock": `0 ${token(
                                                "space.200"
                                            )}`,

                                            "&.h6": {
                                                fontSize: "0.75rem"
                                            }
                                        })}
                                        className="h6"
                                    >
                                        <FormattedMessage {...column.title} />
                                    </span>
                                    <RowGrid numCols={2}>
                                        {column.items.map((link) => {
                                            return (
                                                <div
                                                    key={link.id}
                                                    css={css({
                                                        a: {
                                                            fontWeight: 400,
                                                            fontSize:
                                                                "0.875rem",
                                                            marginBlockEnd:
                                                                "0.5rem",
                                                            lineHeight:
                                                                "1.5rem",
                                                            color: "#101214"
                                                        }
                                                    })}
                                                >
                                                    <Link
                                                        type="anchor"
                                                        href={link.href}
                                                        text={intl.formatMessage(
                                                            link.text
                                                        )}
                                                        dataEventProps={{
                                                            "data-event":
                                                                "clicked",
                                                            "data-event-component":
                                                                "linkButton",
                                                            "data-event-container":
                                                                "globalNavSubnav",
                                                            "data-product-key":
                                                                "home"
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </RowGrid>
                                </LinkColumn>
                            );
                        })}
                    </RowGrid>
                </div>
            </MotionContainer>
        </TypographyStyles>
    );
};
